/**
 * 自社予約サイト料金計算 API ラッパー
 *
 * Endpoint: GET https://n-car.jp/booking/calc/
 *
 * 基本料金・短時間レンタル料金・キャンペーン割引・NOC/CDW・オプション(チャイルド/ブースターシート)
 * を一括で計算し、車両タイプごとの在庫と料金内訳を返す。
 *
 * このサービスが料金計算のソース・オブ・トゥルース。
 * 旧 feePolicy.ts (固定単価 NOC=770/CDW=2200) は廃止。
 */

import { SITE_ORIGIN } from '../constants/calendar';
import { debug } from '../lib/debug';

export interface BookingCalcParams {
  /** 貸出日時 ISO 8601 (YYYY-MM-DDTHH:mm) */
  start: string;
  /** 返却日時 ISO 8601 (YYYY-MM-DDTHH:mm) */
  end: string;
  /** 言語 (デフォルト 'ja') */
  lang?: 'ja' | 'en' | 'kr' | 'zh' | string;
  noc?: boolean;
  cdw?: boolean;
  child_seat?: number;
  booster_seat?: number;
  /** 車両タイプコードで絞り込みたい場合 */
  car_type?: string;
}

export interface BookingCalcBreakdown {
  first_night: number;
  extra_nights: number;
  short: number;
  subtotal_base: number;
  discount: number;
  coupon_applied: boolean;
  fees: number;
  options: number;
  insurance: number;
  grand_total: number;
}

export interface BookingCalcVehicle {
  code: string;
  name: string;
  name_ja: string;
  base_price: number;
  first_night_price: number;
  total_stock: number;
  available_stock: number;
  is_request_reservation: boolean;
  campaign_eligible: boolean;
  etc_card_free: boolean;
  breakdown: BookingCalcBreakdown;
}

export interface BookingCalcCampaign {
  id: number;
  name: string;
  discount_percent: string | number | null;
  etc_free: boolean;
  eligible_car_codes: string[];
  rental_start_from: string;
  rental_end_to: string;
}

export interface BookingCalcResponse {
  ok: boolean;
  request: {
    start: string;
    end: string;
    lang: string;
    options: { noc: boolean; cdw: boolean; child_seat: number; booster_seat: number };
    car_type: string | null;
  };
  duration: { total_minutes: number; nights: number; short: boolean; option_days: number };
  campaign: BookingCalcCampaign | null;
  vehicles: BookingCalcVehicle[];
}

// 計算 API のホスト解決:
// - localhost: 空文字（setupProxy.js が /booking → n-car.jp に中継）
// - その他（本番含む）: 絶対 URL https://n-car.jp
//   ※ SITE_ORIGIN が空文字になると booking.trailernchu.co.jp/booking/calc/ に
//     fall-through して SPA の index.html が返り JSON parse でコケるため明示
const isLocalhost = (): boolean => {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
};

const resolveCalcOrigin = (): string => {
  if (isLocalhost()) return ''; // 相対 URL → setupProxy.js
  // SITE_ORIGIN が空でも production の場合は強制的に絶対 URL にする
  return SITE_ORIGIN || 'https://n-car.jp';
};

const buildUrl = (params: BookingCalcParams): string => {
  const search = new URLSearchParams();
  search.set('start', params.start);
  search.set('end', params.end);
  search.set('lang', params.lang || 'ja');
  if (params.noc !== undefined) search.set('noc', params.noc ? '1' : '0');
  if (params.cdw !== undefined) search.set('cdw', params.cdw ? '1' : '0');
  if (params.child_seat != null) search.set('child_seat', String(params.child_seat));
  if (params.booster_seat != null) search.set('booster_seat', String(params.booster_seat));
  if (params.car_type) search.set('car_type', params.car_type);
  return `${resolveCalcOrigin()}/booking/calc/?${search.toString()}`;
};

/** ISO 8601 整形: 「YYYY-MM-DD HH:mm」「YYYY-MM-DDTHH:mm:SS+09:00」など複数形式を吸収 */
export function toCalcDateTime(input: string | Date): string {
  if (input instanceof Date) {
    const y = input.getFullYear();
    const m = String(input.getMonth() + 1).padStart(2, '0');
    const d = String(input.getDate()).padStart(2, '0');
    const hh = String(input.getHours()).padStart(2, '0');
    const mi = String(input.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mi}`;
  }
  const trimmed = String(input).trim();
  // タイムゾーン部分・秒は削除
  const noTz = trimmed.replace(/(\+|-)\d{2}:\d{2}$/, '').replace(/Z$/, '');
  const noSec = noTz.replace(/(:\d{2}):\d{2}$/, '$1');
  // 空白を T に正規化
  return noSec.replace(' ', 'T');
}

export async function fetchBookingCalc(params: BookingCalcParams): Promise<BookingCalcResponse> {
  const url = buildUrl(params);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '(no-window)';
  const origin = typeof window !== 'undefined' ? window.location.origin : '(no-window)';

  // 診断用に常時出力（debug フラグに依存しない）
  // eslint-disable-next-line no-console
  console.info('[bookingCalc] →', { url, hostname, origin, params });

  let res: Response;
  try {
    res = await fetch(url, { method: 'GET' });
  } catch (e: any) {
    // CORS / ネットワーク失敗はここに落ちる（ブラウザは詳細を伏せるが TypeError になる）
    // eslint-disable-next-line no-console
    console.error('[bookingCalc] fetch failed', { url, hostname, error: e });
    const corsHint = isLocalhost()
      ? ' [ヒント] localhost dev で /booking プロキシが効いていません。dev server コンソールに [proxy] /booking -> https://n-car.jp が出ているか確認し、setupProxy.js 変更後は npm start を完全に再起動してください。'
      : ' [ヒント] CORS によりブラウザが応答を拒否した可能性があります。DevTools の Network タブで /booking/calc/ のステータスを確認してください。';
    throw new Error(`booking calc API: fetch 自体が失敗 (${e?.message || e}) url=${url} hostname=${hostname}${corsHint}`);
  }

  // eslint-disable-next-line no-console
  console.info('[bookingCalc] ←', {
    url,
    status: res.status,
    contentType: res.headers.get('content-type'),
    finalUrl: res.url,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`booking calc API error: ${res.status} ${text.slice(0, 200)} (url=${url})`);
  }
  // content-type で HTML が混入していないか検証（fall-through で index.html が返る事故対策）
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('json')) {
    const preview = await res.text().catch(() => '');
    // eslint-disable-next-line no-console
    console.error('[bookingCalc] non-json response', {
      url,
      contentType,
      finalUrl: res.url,
      preview: preview.slice(0, 200),
    });
    const hint = isLocalhost()
      ? ' [ヒント] localhost dev で /booking プロキシが効いていない可能性があります。dev server コンソールに [proxy] /booking -> https://n-car.jp の表示があるか確認し、無ければ npm start を再起動してください。'
      : '';
    throw new Error(`booking calc API: 想定外の content-type "${contentType}" を受信しました (finalUrl=${res.url})${hint}`);
  }
  const json = (await res.json()) as BookingCalcResponse;
  if (!json.ok) {
    throw new Error('booking calc API returned ok=false');
  }
  debug.log('bookingCalcService', 'fetched', {
    nights: json.duration.nights,
    vehicles: json.vehicles.length,
    campaign: json.campaign?.name,
  });
  return json;
}
